/**
 * TimelineItem component for the Indigo design system.
 *
 * A generic timeline component that displays chronological information with visual hierarchy.
 * Fully customizable through type configuration objects. Supports both display and edit modes.
 *
 * Features:
 * - Two modes: display and edit
 * - Three-column horizontal layout (metadata, icon, content)
 * - Customizable type configurations (icon + color)
 * - Tag support for categories or labels
 * - Markdown support for content description
 * - Vertical line connector for timeline continuity
 * - Edit mode with DatePicker, TextField, and DropdownSelector components
 *
 * @example
 * ```tsx
 * // Display mode
 * <TimelineItem
 *   item={{
 *     id: "1",
 *     type: "event",
 *     date: "01/01/2024",
 *     doctorName: "John Smith",
 *     sourceTag: "AA",
 *     startPageNumber: 1,
 *     endPageNumber: 3,
 *     tags: ["Planning", "Q1"],
 *     content: "Discussed project roadmap..."
 *   }}
 *   typeConfig={myTypeConfig}
 * />
 *
 * // Edit mode
 * <TimelineItem
 *   item={{
 *     id: "1",
 *     type: "event",
 *     date: "01/01/2024",
 *     doctorName: "John Smith",
 *     sourceTag: "AA",
 *     startPageNumber: 1,
 *     endPageNumber: 3,
 *     tags: ["Planning"],
 *     content: "Discussed project roadmap..."
 *   }}
 *   typeConfig={myTypeConfig}
 *   mode="edit"
 *   availableTags={["Planning", "Q1", "Q2", "Budget"]}
 *   onChange={(data) => console.log('Changed:', data)}
 * />
 * ```
 */

import { clsx } from "clsx";
import { memo, useCallback } from "react";
// Import components
import { Checkbox } from "../Checkbox";
import { IconButton } from "../IconButton";
import { toast } from "../Toast";
import "./TimelineItem.css";
import { Icon } from "../Icons";
import { TimelineItemActions } from "./components/TimelineItemActions";
import { TimelineItemContentEdit } from "./components/TimelineItemContentEdit";
import { TimelineItemContentV2 } from "./components/TimelineItemContentV2";
import { TimelineItemIndicatorEdit } from "./components/TimelineItemIndicatorEdit";
import { TimelineItemMetadataEdit } from "./components/TimelineItemMetadataEdit";
import { TimelineItemRibbon } from "./components/TimelineItemRibbon";
import { useTimelineItemExpansion } from "./hooks/useTimelineItem";
import type { TimelineItemAction } from "./types";
// Import types
import { TimelineItemData, TimelineItemProps } from "./types";

/**
 * TimelineItem component
 *
 * Displays a timeline item with metadata, type indicator, and content.
 * Supports both display and edit modes for interactive timeline management.
 *
 * @param props - Component props (@see TimelineItemProps)
 * @returns A React component for displaying timeline items
 */
const TimelineItemComponent = <T extends string = string>({
  // Item data
  item,

  // Configuration and mode
  typeConfig,
  mode = "display",

  // Display options
  showConnector = true,
  defaultExpanded = false,
  selected = false,
  expanded,
  showDiff = false,

  // Callbacks
  onClick,
  onToggleCollapse,
  onChange,
  onActionClick,

  // Edit mode options
  availableTags = [],

  // Misc props
  className = "",
  "data-testid": dataTestId,

  // Search-related props
  searchQuery,
  activeMatchIndex = -1,

  // Keyboard navigation
  isKeyboardFocused = false,

  // Row number
  rowNumber,

  // Suggestion action callback
  onSuggestionAction,

  // Navigate to related segment callback
  onNavigateToSegment,

  // Render extra content slot
  renderValidationIssues,
  citations,
  onOpenCitation,
  onRemoveCitation,

  // Per-item rerun loading state
  isRerunning = false,
  isRecentlyRerun = false,
  isRerunFailed = false,
  showRerunAction = true,
  showFileTicketAction = true,
  rerunTooltip,
  actionVisibilityByRecordType,
  onViewVideoClick,
  maxDurationSec,
}: TimelineItemProps<T>) => {
  // Per-item action visibility overrides keyed by the item's record type.
  // Missing entries default to "visible" — callers only need to list the
  // actions they want hidden for a given record type.
  const recordTypeActionOverrides =
    actionVisibilityByRecordType?.[item.type] ?? undefined;
  const isActionVisible = (
    action: TimelineItemAction,
    defaultValue: boolean,
  ): boolean => {
    const override = recordTypeActionOverrides?.[action];
    return override === undefined ? defaultValue : override;
  };
  // Destructure item data
  const {
    date,
    doctorName,
    sourceTag,
    startPageNumber,
    endPageNumber,
    type,
    title,
    tags = [],
    content,
    originalContent,
    contentResetKey,
    markedForCreation = false,
    markedForDeletion = false,
    isStarred = false,
    deleteSegmentSuggestion,
    textRangeSuggestions,
  } = item;

  // Get configuration for this item type
  const config = typeConfig[type];

  // Video entries (sub-rosa) are identified by string-matching `item.type`
  // — the TimelineItem package is generic over T extends string and
  // shouldn't import the app's RecordType enum, so the literal sentinel
  // is the lowest-coupling option. Drives the edit-mode metadata layout
  // (HH:MM:SS inputs instead of page range) and the footer formatting.
  const isVideoEntry = item.type === "SUB_ROSA";

  // Compute source text:
  //  - sub-rosa: start time and end time
  //  - medical records → "{sourceTag} P{start}-{end}"
  //  - fallback → just the source tag
  const hasPageRange = startPageNumber > 0 || endPageNumber > 0;
  let sourceText: string;
  if (isVideoEntry) {
    const lines: string[] = [];
    if (item.videoStartTime) lines.push(item.videoStartTime);
    if (item.videoEndTime) lines.push(`- ${item.videoEndTime}`);
    else if (item.videoStartTime) lines.push("-");
    sourceText = lines.filter(Boolean).join("\n");
  } else if (hasPageRange) {
    sourceText = `${sourceTag} P${startPageNumber}-${endPageNumber}`;
  } else {
    sourceText = sourceTag;
  }

  // Handle expansion state (controlled or uncontrolled)
  const { isExpanded } = useTimelineItemExpansion(defaultExpanded, expanded);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Only handle clicks in display mode (edit mode uses checkbox for selection)
      if (mode !== "display") return;
      // Do nothing if already selected
      if (selected) return;
      onClick?.(e.nativeEvent.shiftKey);
    },
    [mode, selected, onClick],
  );

  // Handlers for edit mode
  const handleFieldChange = useCallback(
    (field: Partial<TimelineItemData<T>>) => {
      onChange?.(field);
    },
    [onChange],
  );

  // Handler for action button clicks
  const handleActionClick = useCallback(
    (action: TimelineItemAction) => {
      onActionClick?.(action);
    },
    [onActionClick],
  );

  return (
    <div
      className={clsx(
        "indigo-timeline-item",
        mode === "edit" && "indigo-timeline-item--edit",
        mode === "display" && "indigo-timeline-item--display-v2",
        isExpanded && "indigo-timeline-item--expanded",
        markedForDeletion && "indigo-timeline-item--deleted",
        !!deleteSegmentSuggestion &&
          "indigo-timeline-item--has-delete-suggestion",
        selected && "is-selected",
        isStarred && "is-starred",
        isKeyboardFocused && "is-keyboard-focused",
        isRerunning && "indigo-timeline-item--rerunning",
        className,
      )}
      aria-busy={isRerunning || undefined}
      data-testid={dataTestId}
      data-timeline-item-id={item.id}
      onClick={handleClick}
      // Set accessibility role to button because whole item is clickable in display mode
      role={mode === "display" ? "button" : undefined}
      aria-expanded={mode === "display" ? isExpanded : undefined}
      aria-selected={selected}
    >
      {/* Ribbons — V2 display mode only, rendered in priority order:
          important → favorited. The card's border color follows the same
          priority (green > amber-400) via :has() selector ordering in
          TimelineItem.css. */}
      {mode === "display" && (isStarred || item.isImportant) && (
        <div className="indigo-timeline-item__ribbons">
          {item.isImportant && (
            <TimelineItemRibbon
              variant="important"
              label={config?.label ? `Important: ${config.label}` : "Important"}
              icon={<Icon name="lightbulb" size="m" />}
            />
          )}
          {isStarred && (
            <TimelineItemRibbon
              variant="favorite"
              label="Favorited"
              icon={<Icon name="star-filled" />}
            />
          )}
        </div>
      )}

      {mode === "edit" ? (
        <>
          {/* Left column: Metadata */}
          <div className="flex items-start">
            <Checkbox
              className="indigo-timeline-item__checkbox"
              checked={selected}
              onChange={(e) => {
                onClick?.(e.nativeEvent.shiftKey);
              }}
            />
            <TimelineItemMetadataEdit
              date={date}
              sourceTag={sourceTag}
              startPageNumber={startPageNumber}
              endPageNumber={endPageNumber}
              isVideoEntry={isVideoEntry}
              videoStartTime={item.videoStartTime}
              videoEndTime={item.videoEndTime}
              maxDurationSec={isVideoEntry ? maxDurationSec : undefined}
              onVideoStartTimeChange={(value) =>
                handleFieldChange({ videoStartTime: value })
              }
              onVideoEndTimeChange={(value) =>
                handleFieldChange({ videoEndTime: value })
              }
              markedForCreation={markedForCreation}
              markedForDeletion={markedForDeletion}
              onDateChange={(newDate) => handleFieldChange({ date: newDate })}
              onStartPageChange={(value) =>
                handleFieldChange({ startPageNumber: value })
              }
              onEndPageChange={(value) =>
                handleFieldChange({ endPageNumber: value })
              }
              rowNumber={rowNumber}
              deleteSegmentSuggestion={deleteSegmentSuggestion}
              hasTextRangeSuggestions={(textRangeSuggestions?.length ?? 0) > 0}
              textRangeSuggestionsSeen={
                textRangeSuggestions?.every((s) => s.seen) ?? false
              }
              onSuggestionAction={onSuggestionAction}
              onNavigateToSegment={onNavigateToSegment}
              resetKey={contentResetKey}
              isRerunning={isRerunning}
              isRecentlyRerun={isRecentlyRerun}
              isRerunFailed={isRerunFailed}
            />
          </div>

          {/* Middle column: Type indicator with connector */}
          <TimelineItemIndicatorEdit
            type={type}
            typeConfig={typeConfig}
            showConnector={showConnector}
            onTypeChange={(newType) => handleFieldChange({ type: newType })}
          />

          {/* Right column: Content */}
          <TimelineItemContentEdit
            title={title}
            doctorName={doctorName}
            tags={tags}
            availableTags={availableTags}
            content={content}
            originalContent={originalContent}
            contentResetKey={contentResetKey}
            showDiff={showDiff}
            isVideoEntry={isVideoEntry}
            onTitleChange={(value) => handleFieldChange({ title: value })}
            onDoctorNameChange={(value) =>
              handleFieldChange({ doctorName: value })
            }
            onTagsChange={(newTags) => handleFieldChange({ tags: newTags })}
            onContentChange={(value) => handleFieldChange({ content: value })}
            textRangeSuggestions={textRangeSuggestions}
            onNavigateToSegment={onNavigateToSegment}
            onSuggestionAction={onSuggestionAction}
            searchQuery={searchQuery}
            activeMatchIndex={activeMatchIndex}
            citations={citations}
            onOpenCitation={onOpenCitation}
            onRemoveCitation={onRemoveCitation}
            validationIssues={renderValidationIssues?.(item.id)}
            onViewVideoClick={
              isVideoEntry && onViewVideoClick
                ? () => onViewVideoClick(item.id)
                : undefined
            }
          />
        </>
      ) : (
        /* V2 display: single-column top-down layout */
        <TimelineItemContentV2
          date={date}
          title={title}
          doctorName={doctorName}
          content={content}
          sourceText={sourceText}
          pdfFileName={item.displayFileName ?? item.pdfFileName}
          rowNumber={rowNumber}
          entryInfo={item.entryInfo}
          searchQuery={searchQuery}
          activeMatchIndex={activeMatchIndex}
          isExpanded={isExpanded}
          validationIssues={renderValidationIssues?.(item.id)}
          citations={citations}
          onOpenCitation={onOpenCitation}
          onRemoveCitation={onRemoveCitation}
          onFileTicketClick={
            showFileTicketAction
              ? () => handleActionClick("fileTicket")
              : undefined
          }
          onViewVideoClick={
            isVideoEntry && onViewVideoClick
              ? () => onViewVideoClick(item.id)
              : undefined
          }
        />
      )}

      {/* Hover buttons - display mode only */}
      {mode !== "edit" && (
        <div
          className={clsx(
            "indigo-timeline-item__hover-buttons",
            isStarred && "is-starred",
            item.isImportant && "is-important",
          )}
        >
          <IconButton
            className="indigo-timeline-item__collapse-btn"
            iconName={isExpanded ? "collapse" : "expand"}
            variant="ghost"
            size="md"
            tooltip={isExpanded ? "Collapse" : "Expand"}
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse?.();
            }}
          />
          <IconButton
            className="indigo-timeline-item__link-btn"
            iconName="link"
            variant="ghost"
            size="md"
            tooltip="Copy link"
            onClick={(e) => {
              e.stopPropagation();
              const url = new URL(window.location.href);
              // When the entry belongs to a different appointment in the
              // chain (multi-appointment MRR view), rewrite the URL path
              // to point at THAT appointment so the link opens the right
              // briefing context.
              const sourceAppointmentId = item.entryInfo?.appointmentId;
              if (sourceAppointmentId) {
                url.pathname = url.pathname.replace(
                  /\/appointments\/[^/]+/,
                  `/appointments/${sourceAppointmentId}`,
                );
              }
              url.searchParams.set("segmentId", item.id);
              navigator.clipboard
                .writeText(url.toString())
                .then(() => {
                  toast({
                    message: "Link copied to clipboard",
                    duration: 3000,
                  });
                })
                .catch(() => {
                  toast.error("Failed to copy link to clipboard");
                });
            }}
          />
          {/* Star is scoped to the current appointment's annotation table —
              hide the button for entries belonging to other appointments
              in the chain. */}
          {(!item.entryInfo || item.entryInfo.isCurrent) && (
            <IconButton
              className="indigo-timeline-item__star-btn"
              iconName={isStarred ? "starred" : "star"}
              variant="ghost"
              size="md"
              tooltip={isStarred ? "Unstar" : "Star"}
              onClick={(e) => {
                e.stopPropagation();
                handleActionClick("star");
              }}
            />
          )}
          {/* Important toggle — scoped to the current appointment's annotation
              table, same as star. */}
          {(!item.entryInfo || item.entryInfo.isCurrent) && (
            <IconButton
              className={clsx(
                "indigo-timeline-item__important-btn",
                item.isImportant && "is-important",
              )}
              iconName="lightbulb"
              variant="ghost"
              size="md"
              tooltip={item.isImportant ? "Unmark important" : "Mark important"}
              onClick={(e) => {
                e.stopPropagation();
                handleActionClick("important");
              }}
            />
          )}
          {isActionVisible("ocrText", true) && (
            <IconButton
              className="indigo-timeline-item__ocr-text-btn"
              iconName="sticky-note"
              variant="ghost"
              size="md"
              tooltip="View OCR text"
              onClick={(e) => {
                e.stopPropagation();
                handleActionClick("ocrText");
              }}
            />
          )}
        </div>
      )}

      {/* Actions panel - only in edit mode */}
      {mode === "edit" && (
        <TimelineItemActions
          checked={selected}
          markedForCreation={markedForCreation}
          markedForDeletion={markedForDeletion}
          isStarred={isStarred}
          isImportant={item.isImportant}
          onActionClick={handleActionClick}
          showRerunAction={
            isActionVisible("rerun", showRerunAction) && !isRerunning
          }
          showFileTicketAction={isActionVisible(
            "fileTicket",
            showFileTicketAction,
          )}
          showOcrTextAction={isActionVisible("ocrText", true)}
          showPdfAction={isActionVisible("pdf", true)}
          // Sub-rosa rerun resummarizes the video window; there is no split.
          rerunTooltip={isVideoEntry ? "Resummarize" : rerunTooltip}
        />
      )}
    </div>
  );
};

// Memoized version to prevent unnecessary re-renders
// Leverages Immer's structural sharing - only re-renders when item reference changes
export const TimelineItem = memo(
  TimelineItemComponent,
) as typeof TimelineItemComponent;

export default TimelineItem;
